import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    RefreshCw, Users, Building2, ClipboardCheck, DollarSign, Trophy,
    ChevronDown, ChevronUp, Info, X, ArrowUpDown, ArrowUp, ArrowDown, UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAgentAnalytics, getAgentAnalyticsDetail } from '../../../api/kpi';
import { hasSuperAdminAccess } from '../../../utils/roleAccess';
import ReassignIgnoredLeadModal from '../../leads/components/ReassignIgnoredLeadModal';
import CommissionReportPanel from './CommissionReportPanel';
import { PeriodSelector, useAnalyticsPeriod } from './AnalyticsPeriod';
import {
    CLR, TEAL, fmtMoney, fmtMoneyFine, initials, pillPalette, pillTier,
} from './crmAnalyticsUi';

/**
 * Analytics > Agents tab.
 *
 * Full-funnel view of each agent's lead performance, laid out in five grouped
 * columns (design: client mockup leaderboard-v2.html):
 *
 *   Funnel volume  - Appraisals added, Leads assigned, Actioned <24h, Aging leads
 *   Conversion     - Lead→appr, Appr→list, Appr→withdrawn, List→sale
 *   Speed          - Avg days on market
 *   Value          - Sold, Avg commission, Total commission, Revenue / lead
 *   Pipeline       - Current listings, Listed, Withdrawn
 *
 * One sortable row per agent; a team-average footer summarises the agency. Click
 * any drillable cell to see the records behind it, and expand a row for "Where
 * listings come from" / "Leads by source". Reuses the Leaderboard's per-agent
 * engine (kpi-tracker/agents/analytics/) so the shared figures stay consistent.
 */

// Grouped column model. Each column names its data `key`; `drill` opens the
// records modal; `pill` [good, mid] colours a percentage; `denom` blanks a rate
// to "—" when there's nothing to divide by.
// Restructured into a Collings-vs-Agent scorecard (#12870212420): the table
// separates what Collings gave the agent from what the agent generated
// themselves, using the immutable origin attribution. Click any agent's name
// for the full per-agent funnel.
const GROUPS = [
    {
        key: 'pipe', label: 'Pipeline', color: CLR.border,
        cols: [
            { key: 'current_listings', label: 'Current', type: 'num', drill: 'current_listings' },
            { key: 'listings_won', label: 'Listed', type: 'num', drill: 'listings_won' },
            { key: 'listings_lost', label: 'Withdrawn', type: 'num', drill: 'listings_lost' },
            { key: 'appraisals', label: 'Appraisals', type: 'num', drill: 'appraisals' },
        ],
    },
    {
        key: 'collings', label: 'Collings Leads', color: CLR.aqua,
        cols: [
            { key: 'collings_leads', label: 'Received', type: 'num', drill: 'collings_leads' },
            { key: 'collings_contacted', label: 'Contacted', type: 'num', drill: 'collings_leads' },
            { key: 'collings_leads_ignored', label: 'Ignored', type: 'num', drill: 'leads_ignored', drillParams: { origin: 'collings_generated' } },
            { key: 'collings_lead_to_appraisal_rate', label: '→Appraisal', type: 'pill', pill: [35, 20], denom: 'collings_leads', drill: 'collings_leads' },
            { key: 'collings_leads_to_database', label: '→Database', type: 'num', drill: 'leads_to_database', drillParams: { origin: 'collings_generated' } },
        ],
    },
    {
        key: 'agentgen', label: 'Agent Generated', color: CLR.aquaDark,
        cols: [
            { key: 'agent_leads', label: 'Leads created', type: 'num', drill: 'agent_leads' },
            { key: 'agent_appraisals', label: 'Appraisals', type: 'num', drill: 'appraisals', drillParams: { origin: 'agent_generated' } },
            { key: 'agent_leads_to_database', label: 'Database', type: 'num', drill: 'leads_to_database', drillParams: { origin: 'agent_generated' } },
            { key: 'agent_appraisal_to_listing_rate', label: '→Listing', type: 'pill', pill: [70, 40], denom: 'agent_appraisals', drill: 'listings_won', drillParams: { origin: 'agent_generated' } },
        ],
    },
    {
        key: 'conv', label: 'Conversion', color: CLR.aquaDark,
        cols: [
            { key: 'appraisal_to_listing_rate', label: 'Appr→list', type: 'pill', pill: [70, 40], denom: 'appraisals', drill: 'listings_won' },
            { key: 'listing_to_sale_rate', label: 'List→sale', type: 'pill', pill: [60, 40], denom: 'listings_won', drill: 'commissions' },
            { key: 'appraisal_to_withdrawn_rate', label: 'Appr→withdrawn', type: 'pill', pill: [15, 35], invert: true, denom: 'appraisals', drill: 'appraisals_withdrawn' },
            { key: 'lead_to_ignored_rate', label: 'Lead→ignored', type: 'pill', pill: [10, 25], invert: true, denom: 'leads_generated', drill: 'leads_ignored' },
            // Lost sits next to Ignored so the two read together: a real lead we
            // lost vs. rubbish we threw out (Monday 13058325657).
            { key: 'lead_to_lost_rate', label: 'Lead→lost', type: 'pill', pill: [10, 25], invert: true, denom: 'leads_generated', drill: 'leads_lost_genuine' },
        ],
    },
    {
        key: 'speed', label: 'Speed', color: CLR.gray,
        cols: [
            { key: 'collings_avg_response_hours', label: 'Avg response', type: 'suffix', suffix: 'h', drill: 'collings_leads' },
            { key: 'avg_days_on_market', label: 'Avg DOM', type: 'suffix', suffix: 'd', drill: 'current_listings' },
        ],
    },
    {
        key: 'value', label: 'Value', color: CLR.black,
        cols: [
            { key: 'properties_sold', label: 'Sold', type: 'num', drill: 'commissions' },
            { key: 'total_commission', label: 'Total GCI', type: 'money', drill: 'commissions' },
            { key: 'collings_commission', label: 'Collings GCI', type: 'money', drill: 'commissions' },
            { key: 'agent_commission', label: 'Agent GCI', type: 'money', drill: 'commissions' },
            { key: 'agency_contribution_pct', label: 'Agency %', type: 'pill', pill: [40, 20], denom: 'total_commission', drill: 'commissions' },
        ],
    },
];
const ALL_COLS = GROUPS.flatMap((g) => g.cols);
const COL_COUNT = ALL_COLS.length;

// Which side of the business to report on — see CustomUser.department (BE).
// There is no rental-lead equivalent of a vendor Lead today, so a 'rentals'
// request zeroes out the lead-funnel columns rather than showing sales data.
const SEGMENT_TABS = [
    { value: 'sales', label: 'Sales' },
    { value: 'rentals', label: 'Rentals' },
];

const SegmentToggle = ({ segment, onChange }) => (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
        {SEGMENT_TABS.map((tab) => (
            <button
                key={tab.value}
                type="button"
                onClick={() => onChange(tab.value)}
                className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                    segment === tab.value ? 'bg-[#55C5D0] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
            >
                {tab.label}
            </button>
        ))}
    </div>
);

// Section order + labels for drill-downs whose rows carry a `bucket` field —
// today that's the Current listings drill (CG, 2026-08-31: "breakdown the off
// market listed & on market listed like we did" — mirroring the Pipeline Value
// breakdown). Rows without a bucket render as the plain flat list unchanged.
const LISTING_BUCKETS = [
    { key: 'on_market', label: 'On Market Listed' },
    { key: 'off_market', label: 'Off Market Listed' },
];

export const DetailModal = ({ open, onClose, title, loading, rows, canReassign = false, metric = '', onReassign }) => {
    if (!open) return null;
    const grouped = rows.some((r) => r.bucket);
    // Only ignored-lead rows can be re-assigned, and only by a super admin.
    const rowReassignable = (r) => canReassign && metric === 'leads_ignored' && r.kind === 'lead';
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 py-10">No records in this period.</p>
                    ) : grouped ? (
                        LISTING_BUCKETS.map(({ key, label }) => {
                            // Rows missing a bucket (shouldn't happen for this
                            // drill) fall into On Market rather than vanishing.
                            const bucketRows = rows.filter((r) => (r.bucket || 'on_market') === key);
                            if (bucketRows.length === 0) return null;
                            const subtotal = bucketRows.reduce((sum, r) => sum + (Number(r.price) || 0), 0);
                            return (
                                <div key={key} className="mb-4 last:mb-0">
                                    <div className="flex items-center justify-between px-1 mb-1">
                                        <h4 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                                            {label} <span className="text-gray-400 font-normal">({bucketRows.length})</span>
                                        </h4>
                                        {subtotal > 0 && (
                                            <span className="text-[11px] font-semibold text-gray-500 tabular-nums">
                                                {fmtMoney(subtotal)}
                                            </span>
                                        )}
                                    </div>
                                    <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                                        {bucketRows.map((r) => <DetailRow key={`${r.kind}-${r.id}`} r={r} reassignable={rowReassignable(r)} onReassign={onReassign} />)}
                                    </ul>
                                </div>
                            );
                        })
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {rows.map((r) => <DetailRow key={`${r.kind}-${r.id}`} r={r} reassignable={rowReassignable(r)} onReassign={onReassign} />)}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

const DetailRow = ({ r, reassignable = false, onReassign }) => {
    // Property rows open the record itself in a new tab. Commission rows carry a
    // composite "source:id" (not a Property pk) and leads have no property page,
    // so only a plain numeric property id becomes a link.
    const propertyId = r.kind === 'property' && /^\d+$/.test(String(r.id)) ? r.id : null;
    const label = r.address || (r.kind === 'lead' ? (r.name || 'Lead - no property') : `Property ${r.id}`);
    // The note the agent typed when they ignored the lead / withdrew the listing.
    // It was captured but never shown here (CG, 2026-09-16: "Cant read why leads
    // were ignored when marked as other in analytics"), so it prints in full.
    // `note` is the note / message text behind a Prospecting or Communications
    // row on the per-agent page (Monday 13225628427).
    const note = r.ignore_notes || r.withdrawn_reason_note || r.note;
    return (
    <li className="py-2.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">
                {propertyId ? (
                    <a
                        href={`/properties/${propertyId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                        style={{ color: TEAL }}
                        title="Open this property"
                    >
                        {label}
                    </a>
                ) : label}
            </div>
            <div className="text-[11px] text-gray-500 truncate">
                {[
                    r.agent_name,
                    ...(r.kind === 'lead' ? [r.address ? r.name : null, r.stage, r.source] : [r.state, r.status, r.origin_label, r.source_label, r.withdrawn_reason]),
                ].filter(Boolean).join(' • ')}
            </div>
            {/* Why the lead was ignored / lost, on its own line so the truncated
                line above never cuts it off ("Please write why leads are being
                ignored", CG, Monday 13225628427, 12 Oct 2026). */}
            {r.kind === 'lead' && (r.ignore_reason || r.lost_reason) && (
                <div className="text-[11px] mt-0.5">
                    <span className="font-semibold text-red-600">{r.ignore_reason ? 'Why ignored: ' : 'Why lost: '}</span>
                    <span className="text-gray-700">{r.ignore_reason || r.lost_reason}</span>
                </div>
            )}
            {note && (
                <div className="text-[11px] text-gray-600 italic mt-1 whitespace-pre-wrap break-words">
                    <span className="not-italic font-semibold text-gray-500">Note: </span>{note}
                </div>
            )}
        </div>
        <div className="text-right flex-shrink-0">
            {r.commission != null && (
                <div className="text-sm font-semibold text-green-700 tabular-nums">{fmtMoney(r.commission)}</div>
            )}
            {r.price && r.commission == null && (
                <div className="text-sm text-gray-700 tabular-nums">{fmtMoney(r.price)}</div>
            )}
            {r.date && <div className="text-[11px] text-gray-400">{r.date}</div>}
            {reassignable && (
                <button
                    onClick={() => onReassign && onReassign(r)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border border-[#55C5D0] text-[#3f9ba4] hover:bg-[#e6f7f9]"
                    title="Re-assign this ignored lead to an agent"
                >
                    <UserCheck className="w-3 h-3" /> Re-assign
                </button>
            )}
        </div>
    </li>
    );
};

// "Why listings were lost" / "Why appraisals were withdrawn" breakdown.
// Every reason is a button that opens the properties behind it (CG,
// 2026-09-03: "make ... clickable to show which property"). `variant` matches
// the two places this renders: the agency-wide strip under the summary cards
// and the smaller chips inside an expanded agent row.
export const ReasonPills = ({ reasons, onPick, variant = 'strip', noun = 'property', nounPlural = 'properties' }) => {
    const base = variant === 'strip'
        ? 'px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200'
        : 'text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700';
    return (
        <>
            {reasons.map((r) => (
                <button
                    key={r.reason}
                    type="button"
                    onClick={() => onPick(r.reason)}
                    title={`Show the ${r.count} ${r.count === 1 ? noun : nounPlural} · ${r.reason}`}
                    className={`${base} cursor-pointer transition-colors hover:bg-gray-200 hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400`}
                >
                    {r.reason} <span className="font-semibold text-gray-900">{r.count}</span>
                </button>
            ))}
        </>
    );
};

const SummaryCard = ({ icon, iconBg, iconColor, label, value, sub, onClick }) => (
    <div
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        className={`bg-white rounded-lg border border-gray-100 shadow-sm p-4 ${onClick ? 'cursor-pointer hover:border-gray-300 hover:shadow-md transition' : ''}`}
    >
        <div className="flex items-center gap-2 mb-1.5">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: iconBg, color: iconColor }}>{icon}</span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

// ---- Grouped-table cell styles (echo the mockup) ----
const thGroup = (color) => ({
    background: CLR.grayLight, color: CLR.gray, fontWeight: 600, fontSize: 10.5,
    letterSpacing: '.04em', textTransform: 'uppercase', padding: '8px 10px',
    borderBottom: `1px solid ${CLR.border}`, textAlign: 'center', borderLeft: `2px solid ${color}`,
});
const thSub = (groupStart, color) => ({
    background: CLR.white, color: CLR.black, fontWeight: 700, fontSize: 12.5,
    padding: '8px 9px', borderBottom: `2px solid ${CLR.border}`, textAlign: 'right',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
    ...(groupStart ? { borderLeft: `2px solid ${color}` } : {}),
});
const tdCell = { padding: '7px 9px', textAlign: 'right', borderBottom: `1px solid ${CLR.border}`, whiteSpace: 'nowrap', color: CLR.black };
const stickyAgentBase = { position: 'sticky', left: 0, borderRight: `1px solid ${CLR.border}`, textAlign: 'left' };

// `reloadToken` — bump from the parent to force a refetch (see AgentUsagePanel).
const AgentAnalyticsPanel = ({ reloadToken = 0 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [helpOpen, setHelpOpen] = useState(false);
    const [segment, setSegment] = useState('sales');
    const [expanded, setExpanded] = useState(null);
    const [sort, setSort] = useState({ key: 'total_commission', dir: 'desc' });
    const [detail, setDetail] = useState({ open: false, loading: false, title: '', rows: [], metric: '' });
    // Super admins can re-assign an ignored lead from its drill-in (#12984261525).
    const { user } = useSelector((s) => s.auth || {});
    const canReassign = hasSuperAdminAccess(user);
    const [reassignRow, setReassignRow] = useState(null);
    const [localReload, setLocalReload] = useState(0);
    const loadTokenRef = useRef(0);

    // Period selector — shared with the other CRM Analytics tabs. Defaults to
    // the current financial year and auto-rolls on 1 Jul.
    const period = useAnalyticsPeriod();
    const { range } = period;
    const navigate = useNavigate();

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        getAgentAnalytics({ start_date: range.start, end_date: range.end, segment })
            .then((res) => { if (myToken === loadTokenRef.current) setData(res.data); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('Agent analytics load failed:', err);
                toast.error('Failed to load agent analytics');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [range.start, range.end, segment, reloadToken, localReload]);

    // Agent options for the re-assign picker, from the same leaderboard data.
    const agentOptions = useMemo(
        () => (data?.agents || [])
            .map((a) => ({ id: a.agent_id, name: a.agent_name }))
            .filter((a) => a.id && a.name),
        [data],
    );

    const agents = useMemo(() => {
        const list = [...(data?.agents || [])];
        const { key, dir } = sort;
        list.sort((a, b) => {
            const av = a[key] ?? 0, bv = b[key] ?? 0;
            if (typeof av === 'string' || typeof bv === 'string') {
                return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
            }
            return dir === 'asc' ? av - bv : bv - av;
        });
        return list;
    }, [data, sort]);

    const totals = data?.totals || {};

    const toggleSort = (key) => {
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
    };

    const runDetail = (params, title) => {
        setDetail({ open: true, loading: true, title, rows: [], metric: params.metric || '' });
        getAgentAnalyticsDetail({ ...params, start_date: range.start, end_date: range.end, segment })
            .then((res) => setDetail((d) => ({ ...d, loading: false, rows: res.data?.results || [] })))
            .catch((err) => {
                console.error('Agent detail load failed:', err);
                toast.error('Failed to load records');
                setDetail((d) => ({ ...d, loading: false, rows: [] }));
            });
    };

    // Per-agent cell -> that agent; summary card -> all agents (agency-wide).
    const openDrill = (agent, col) => {
        if (!col.drill) return;
        runDetail(
            { agent: agent.agent_id, metric: col.drill, ...(col.drillParams || {}) },
            `${agent.agent_name} · ${col.label}`,
        );
    };
    const openTotalDrill = (metric, label) => runDetail({ metric }, `All agents · ${label}`);

    // One withdrawal reason -> the properties behind it. `agent` omitted for
    // the agency-wide strip under the summary cards.
    const openReasonDrill = (metric, label, reason, agent = null) => runDetail(
        agent ? { agent: agent.agent_id, metric, reason } : { metric, reason },
        `${agent ? agent.agent_name : 'All agents'} · ${label} · ${reason}`,
    );

    const SortIcon = ({ colKey }) => {
        if (sort.key !== colKey) return <ArrowUpDown className="w-3 h-3 inline-block opacity-40" />;
        return sort.dir === 'asc'
            ? <ArrowUp className="w-3 h-3 inline-block" style={{ color: TEAL }} />
            : <ArrowDown className="w-3 h-3 inline-block" style={{ color: TEAL }} />;
    };

    // Render one grouped-table cell for an agent row.
    const renderCell = (row, col) => {
        const raw = row[col.key];
        const clickable = col.drill && Number(row[col.key]) !== 0;

        // Percentage pill — blanks to "—" when there's no denominator.
        if (col.type === 'pill') {
            const denom = col.denom ? Number(row[col.denom] || 0) : 1;
            if (!denom) return <span style={{ color: CLR.gray }}>—</span>;
            const v = Number(raw || 0);
            const tier = pillTier(v, col.pill, col.invert);
            const { bg, fg } = pillPalette[tier];
            const pill = (
                <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>
                    {v}%
                </span>
            );
            return col.drill ? (
                <button onClick={() => openDrill(row, col)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} title="View records">
                    {pill}
                </button>
            ) : pill;
        }

        // Aging-leads flag: red when there are stale leads, muted 0 otherwise.
        if (col.type === 'flag') {
            const v = Number(raw || 0);
            if (v <= 0) return <span style={{ color: CLR.gray, fontWeight: 400 }}>0</span>;
            const flag = (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: CLR.red }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: CLR.red, display: 'inline-block' }} />
                    {v}
                </span>
            );
            return col.drill ? (
                <button onClick={() => openDrill(row, col)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} title="View aging leads">
                    {flag}
                </button>
            ) : flag;
        }

        // Blank scalar to "—" when its denominator is empty (e.g. Rev / lead
        // with no leads) so it doesn't read as a real $0.
        if (col.denom && !Number(row[col.denom] || 0)) {
            return <span style={{ color: CLR.gray }}>—</span>;
        }

        // Formatted scalar (money / suffix / plain number).
        let text;
        if (col.type === 'money') text = fmtMoney(raw);
        else if (col.type === 'moneyFine') text = fmtMoneyFine(raw);
        else if (col.type === 'suffix') text = `${raw ?? 0}${col.suffix}`;
        else text = raw ?? 0;

        if (clickable) {
            return (
                <button
                    onClick={() => openDrill(row, col)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: CLR.text, textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.18)', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}
                    title="View records"
                >
                    {text}
                </button>
            );
        }
        return <span style={{ color: CLR.black, fontWeight: 500 }}>{text}</span>;
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading agent analytics…
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Office-wide commission overview + Top Performers leaderboard.
                Moved here from the Portal report page (Monday 13058606178). Self-
                contained with its own FY selector, so it sits above this panel's
                own period-scoped figures. */}
            <CommissionReportPanel />

            <div className="flex items-center gap-3 flex-wrap">
                {/* Shared with CRM Overview and Agent Usage — see AnalyticsPeriod.js */}
                <PeriodSelector period={period} />
                <SegmentToggle segment={segment} onChange={setSegment} />
            </div>

            <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                className="w-full text-left rounded-lg px-3 py-2.5 flex gap-2 items-start border transition-colors hover:bg-gray-50"
                style={{ background: `${TEAL}0f`, borderColor: `${TEAL}33` }}
            >
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: TEAL }} />
                <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-gray-700">
                        How agent analytics are tracked
                        <span className="text-gray-400 font-normal ml-1">{helpOpen ? '▲' : '▼'}</span>
                    </span>
                    {helpOpen && (
                        <p className="text-xs text-gray-600 leading-relaxed mt-1.5">
                            Every figure is scoped to the selected period. <b>Appraisals added</b> is how many
                            appraisals the agent booked onto a property in the period. <b>Leads assigned</b> are
                            selling (vendor) leads only - buyer enquiries are excluded, matching the Leaderboard's
                            Selling Leads.
                            <b>Actioned &lt;24h</b> is the share of those leads first contacted within a day, and
                            <b>Aging leads</b> is how many are still open and untouched 14+ days. <b>Current</b> counts
                            every listing the agent carried at any point in the period, on-market and off-market,
                            excluding withdrawn (those sit in <b>Withdrawn</b>); <b>Avg DOM</b> is their days on market.
                            <b>Current / Listed / Withdrawn</b> are credited to the agent who is first on the listing -
                            taken from the live REA listing where there is one, otherwise the Listing Agent on the record.
                            <b>Appr→withdrawn</b> is the share of an agent's appraisals lost or not proceeded with -
                            these never became a listing, so they're separate from the Pipeline <b>Withdrawn</b> count.
                            <b>Lead→appr / Appr→list / List→sale</b> are the funnel conversion rates (capped at 100%);
                            <b>Rev / lead</b> is total commission ÷ leads assigned. Commission mirrors the Leaderboard
                            exactly (address-deduplicated, co-agent split honoured). Expand a row for where listings and
                            leads come from; click any underlined figure or pill for the records behind it,
                            including a withdrawal reason for the properties it covers.
                        </p>
                    )}
                </div>
            </button>

            {/* Results region — dims with an "Updating…" overlay while a period
                change is loading (the full-screen spinner only covers first load). */}
            <div className="relative">
              {loading && data && (
                <div className="absolute inset-0 z-20 flex items-start justify-center pt-20 bg-white/60 rounded-lg">
                    <span className="inline-flex items-center gap-2 text-sm text-gray-600 bg-white px-3 py-1.5 rounded-full shadow-md border border-gray-100">
                        <RefreshCw className="w-4 h-4 animate-spin" style={{ color: TEAL }} /> Updating…
                    </span>
                </div>
              )}
              <div className={`space-y-5 ${loading && data ? 'opacity-40 pointer-events-none' : ''}`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard icon={<Users className="w-3.5 h-3.5" />} iconBg={`${TEAL}1a`} iconColor={TEAL}
                    label="Agents" value={totals.agents ?? 0} sub="active sales agents" />
                <SummaryCard icon={<Building2 className="w-3.5 h-3.5" />} iconBg="#2563eb1a" iconColor="#2563eb"
                    label="Current listings" value={totals.current_listings ?? 0} sub="on + off market in period"
                    onClick={() => openTotalDrill('current_listings', 'Current listings')} />
                <SummaryCard icon={<Trophy className="w-3.5 h-3.5" />} iconBg="#f59e0b1a" iconColor="#d97706"
                    label="Listings listed" value={totals.listings_won ?? 0} sub={`${totals.listings_lost ?? 0} withdrawn`}
                    onClick={() => openTotalDrill('listings_won', 'Listings listed')} />
                <SummaryCard icon={<ClipboardCheck className="w-3.5 h-3.5" />} iconBg="#0891b21a" iconColor="#0891b2"
                    label="Appraisals" value={totals.appraisals ?? 0} sub={`${totals.appraisals_withdrawn ?? 0} withdrawn`}
                    onClick={() => openTotalDrill('appraisals', 'Appraisals')} />
                <SummaryCard icon={<Users className="w-3.5 h-3.5" />} iconBg="#7c3aed1a" iconColor="#7c3aed"
                    label="Selling leads" value={totals.leads_generated ?? 0} sub={`${totals.actioned_24h_rate ?? 0}% actioned <24h`}
                    onClick={() => openTotalDrill('leads_generated', 'Selling leads')} />
                <SummaryCard icon={<DollarSign className="w-3.5 h-3.5" />} iconBg="#15803d1a" iconColor="#15803d"
                    label="Commission" value={fmtMoney(totals.total_commission)} sub="total earned"
                    onClick={() => openTotalDrill('commissions', 'Commission')} />
            </div>

            {totals.listings_lost_reasons?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="text-gray-500 font-medium uppercase tracking-wide">Why listings were lost:</span>
                    <ReasonPills
                        reasons={totals.listings_lost_reasons}
                        onPick={(reason) => openReasonDrill('listings_lost', 'Listings withdrawn', reason)}
                    />
                </div>
            )}

            {totals.appraisals_withdrawn_reasons?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="text-gray-500 font-medium uppercase tracking-wide">Why appraisals were withdrawn:</span>
                    <ReasonPills
                        reasons={totals.appraisals_withdrawn_reasons}
                        onPick={(reason) => openReasonDrill('appraisals_withdrawn', 'Appraisals withdrawn', reason)}
                    />
                </div>
            )}

            {totals.leads_ignored_reasons?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="text-gray-500 font-medium uppercase tracking-wide">Why leads were ignored:</span>
                    <ReasonPills
                        reasons={totals.leads_ignored_reasons}
                        noun="lead" nounPlural="leads"
                        onPick={(reason) => openReasonDrill('leads_ignored', 'Leads ignored', reason)}
                    />
                </div>
            )}

            {/* Lost is a GENUINE lead that did not convert, kept apart from
                Ignored (rubbish). Christian Gravias: "Please make sure we are
                also tracking lost in the analytics" (Monday 13058325657). */}
            {totals.leads_lost_reasons?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="text-gray-500 font-medium uppercase tracking-wide">Why leads were lost:</span>
                    <ReasonPills
                        reasons={totals.leads_lost_reasons}
                        noun="lead" nounPlural="leads"
                        onPick={(reason) => openReasonDrill('leads_lost_genuine', 'Leads lost', reason)}
                    />
                </div>
            )}

            {/* Legend for the grouped columns. */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-sm font-semibold text-gray-900">Agent lead performance</div>
                    <div className="text-xs text-gray-500">Full-funnel view: lead intake through settlement.</div>
                </div>
                <div className="flex items-center gap-3.5 flex-wrap text-[11px]" style={{ color: CLR.gray }}>
                    {GROUPS.map((g) => (
                        <span key={g.key} className="inline-flex items-center gap-1.5">
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, display: 'inline-block' }} />
                            {g.label}
                        </span>
                    ))}
                </div>
            </div>

            {agents.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-10 text-center">
                    <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">No agent activity in this range yet</p>
                    <p className="text-xs text-gray-500 mt-1">Try a wider date range from the selector above.</p>
                </div>
            ) : (
                <div style={{ border: `1px solid ${CLR.border}`, borderRadius: 10, overflowX: 'auto' }}>
                    {/* width:auto (not 100%) so columns pack to their content instead of
                        stretching across the panel; scrolls on narrow screens. */}
                    <table style={{ borderCollapse: 'collapse', width: 'auto', fontSize: 13.5 }}>
                        <thead>
                            {/* Group row */}
                            <tr>
                                <th style={{ ...stickyAgentBase, background: CLR.white, zIndex: 3, borderBottom: `1px solid ${CLR.border}` }} />
                                {GROUPS.map((g) => (
                                    <th key={g.key} colSpan={g.cols.length} style={thGroup(g.color)}>{g.label}</th>
                                ))}
                            </tr>
                            {/* Sub row (sortable) */}
                            <tr>
                                <th style={{ ...stickyAgentBase, background: CLR.white, zIndex: 3, color: CLR.gray, fontWeight: 600, fontSize: 11, padding: '8px 10px', borderBottom: `2px solid ${CLR.border}` }}>
                                    Agent
                                </th>
                                {GROUPS.map((g) =>
                                    g.cols.map((col, ci) => (
                                        <th
                                            key={col.key}
                                            title={col.label}
                                            style={thSub(ci === 0, g.color)}
                                            onClick={() => toggleSort(col.key)}
                                        >
                                            {col.label} <SortIcon colKey={col.key} />
                                        </th>
                                    ))
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {agents.map((row, i) => (
                                <React.Fragment key={row.agent_id}>
                                    <tr className="hover:bg-[#fafbfb]">
                                        <td style={{ ...tdCell, ...stickyAgentBase, background: CLR.white, fontWeight: 600, zIndex: 1 }}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <button
                                                    onClick={() => setExpanded((e) => (e === row.agent_id ? null : row.agent_id))}
                                                    className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                                                    title="Show sources"
                                                >
                                                    {expanded === row.agent_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                                <span className="tabular-nums text-gray-400 w-4 text-right flex-shrink-0">{i + 1}</span>
                                                <span className="flex items-center justify-center rounded-full flex-shrink-0"
                                                    style={{ width: 22, height: 22, background: CLR.aqua, color: CLR.white, fontSize: 10, fontWeight: 700 }}>
                                                    {initials(row.agent_name)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/analytics/agent/${row.agent_id}`)}
                                                    title={`Open ${row.agent_name}'s dashboard`}
                                                    className="text-gray-800 font-semibold truncate max-w-[150px] hover:text-[#128f89] hover:underline text-left"
                                                >{row.agent_name}</button>
                                            </div>
                                        </td>
                                        {ALL_COLS.map((col) => (
                                            <td key={col.key} style={{ ...tdCell }} className="tabular-nums">
                                                {renderCell(row, col)}
                                            </td>
                                        ))}
                                    </tr>
                                    {expanded === row.agent_id && (
                                        <tr style={{ background: 'var(--c-panel-subtle)' }}>
                                            <td colSpan={COL_COUNT + 1} className="px-6 py-3">
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Where listings come from</div>
                                                        {row.listing_sources?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {row.listing_sources.map((s) => (
                                                                    <span key={s.source} className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700">
                                                                        {s.source} <span className="font-semibold text-gray-900">{s.count}</span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No listings won in this period.</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Leads by source</div>
                                                        {row.leads_by_source?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {row.leads_by_source.map((s) => (
                                                                    <span key={s.source} className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700">
                                                                        {s.label} <span className="font-semibold text-gray-900">{s.count}</span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No leads in this period.</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Why listings were lost</div>
                                                        {row.listings_lost_reasons?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                <ReasonPills
                                                                    variant="chip"
                                                                    reasons={row.listings_lost_reasons}
                                                                    onPick={(reason) => openReasonDrill('listings_lost', 'Listings withdrawn', reason, row)}
                                                                />
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No listings withdrawn in this period.</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Why appraisals were withdrawn</div>
                                                        {row.appraisals_withdrawn_reasons?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                <ReasonPills
                                                                    variant="chip"
                                                                    reasons={row.appraisals_withdrawn_reasons}
                                                                    onPick={(reason) => openReasonDrill('appraisals_withdrawn', 'Appraisals withdrawn', reason, row)}
                                                                />
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No appraisals withdrawn in this period.</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Why leads were ignored</div>
                                                        {row.leads_ignored_reasons?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                <ReasonPills
                                                                    variant="chip"
                                                                    noun="lead" nounPlural="leads"
                                                                    reasons={row.leads_ignored_reasons}
                                                                    onPick={(reason) => openReasonDrill('leads_ignored', 'Leads ignored', reason, row)}
                                                                />
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No leads ignored in this period.</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Why leads were lost</div>
                                                        {row.leads_lost_reasons?.length ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                <ReasonPills
                                                                    variant="chip"
                                                                    noun="lead" nounPlural="leads"
                                                                    reasons={row.leads_lost_reasons}
                                                                    onPick={(reason) => openReasonDrill('leads_lost_genuine', 'Leads lost', reason, row)}
                                                                />
                                                            </div>
                                                        ) : <span className="text-xs text-gray-400">No leads lost in this period.</span>}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td style={{ ...stickyAgentBase, background: CLR.grayLight, zIndex: 1, padding: '8px 10px', fontSize: 10.5, color: CLR.gray, fontWeight: 600, borderTop: `1px solid ${CLR.border}` }}>
                                    Team avg
                                </td>
                                <td colSpan={3} style={footCell}>
                                    {totals.current_listings ?? 0} current · {totals.listings_won ?? 0} listed · {totals.listings_lost ?? 0} withdrawn
                                </td>
                                <td colSpan={4} style={footCell}>
                                    {totals.appraisals ?? 0} appraisals · {totals.leads_generated ?? 0} leads · {totals.actioned_24h_rate ?? 0}% actioned &lt;24h · {totals.aging_leads ?? 0} aging
                                </td>
                                <td colSpan={5} style={footCell}>
                                    Lead→appr {totals.lead_to_appraisal_rate ?? 0}% · Lead→DB {totals.lead_to_database_rate ?? 0}% · Appr→list {totals.appraisal_to_listing_rate ?? 0}% · Appr→withdrawn {totals.appraisal_to_withdrawn_rate ?? 0}% · List→sale {totals.listing_to_sale_rate ?? 0}% · Lead→ignored {totals.lead_to_ignored_rate ?? 0}%
                                </td>
                                <td colSpan={1} style={footCell}>DOM {totals.avg_days_on_market ?? 0}d</td>
                                <td colSpan={4} style={footCell}>
                                    {totals.properties_sold ?? 0} sold · {fmtMoney(totals.avg_commission_per_agent)} avg/agent · {fmtMoney(totals.total_commission)} total
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
              </div>
            </div>

            <DetailModal
                open={detail.open}
                onClose={() => setDetail((d) => ({ ...d, open: false }))}
                title={detail.title}
                loading={detail.loading}
                rows={detail.rows}
                canReassign={canReassign}
                metric={detail.metric}
                onReassign={setReassignRow}
            />

            <ReassignIgnoredLeadModal
                open={!!reassignRow}
                lead={reassignRow}
                agents={agentOptions}
                onClose={() => setReassignRow(null)}
                onReassigned={(lead) => {
                    // Drop the re-assigned lead from the open drill immediately,
                    // and refresh the leaderboard so the Ignored counts follow.
                    setDetail((d) => ({
                        ...d,
                        rows: d.rows.filter((x) => !(x.kind === lead.kind && String(x.id) === String(lead.id))),
                    }));
                    setLocalReload((t) => t + 1);
                }}
            />
        </div>
    );
};

const footCell = {
    padding: '8px 10px', fontSize: 10.5, color: CLR.gray, textAlign: 'left',
    borderTop: `1px solid ${CLR.border}`, background: CLR.grayLight,
};

export default AgentAnalyticsPanel;
